import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateShort } from '../../../../../utils/dateUtils';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { ExpenseGroup } from '../../../__generated__/graphql';
import { getTripLocation, isTrip, ExpenseItem } from './expenseTileUtils';

interface ExpenseTileMetadataProps {
  item: ExpenseItem;
  displayEmployeeName: boolean;
  showSubmitButton: boolean;
  isExpanded: boolean;
  isDeleting: boolean;
  isSubmitting: boolean;
  childExpensesCount: number;
  onSubmitClick: (e: React.MouseEvent) => void;
}

const renderDateRange = (item: ExpenseItem): string | null => {
  if (item.isGroup) {
    if (item.startDate && item.endDate) {
      const start = formatDateShort(item.startDate);
      const end = formatDateShort(item.endDate);
      return start === end ? start : `${start} - ${end}`;
    }
    if (item.startDate) {
      return formatDateShort(item.startDate);
    }
    return null;
  } else {
    return formatDateShort(item.startDate as Date);
  }
};

export const ExpenseTileMetadata: React.FC<ExpenseTileMetadataProps> = ({
  item,
  displayEmployeeName,
  showSubmitButton,
  isExpanded,
  isDeleting,
  isSubmitting,
  childExpensesCount,
  onSubmitClick,
}) => {
  const { t } = useTranslation();
  const dateRange = renderDateRange(item);

  return (
    <div className="expense-tile-meta ms-4">
      <div className="expense-tile-info ms-4">
        {displayEmployeeName && (
          <>
            <FlairIcon icon="profile-outline" size="xs" />
            <span className="expense-tile-subtext">{item.employee.Name}</span>
          </>
        )}
        {item.isGroup && isTrip(item as ExpenseGroup) && (
          <>
            <FlairIcon icon="location-outline" size="xs" />
            <span className="expense-tile-subtext">
              {getTripLocation(item as ExpenseGroup, t)}
            </span>
          </>
        )}
        {dateRange && (
          <>
            <FlairIcon icon="calendar-clear-outline" size="xs" />
            <span className="expense-tile-subtext">{dateRange}</span>
          </>
        )}
      </div>
      {showSubmitButton && (
        <div
          className={` ${
            isExpanded
              ? 'expense-tile-submit-btn-open'
              : 'expense-tile-submit-btn'
          }`}>
          <button
            type="button"
            className="btn btn-primary btn-sm expense-submit-btn mt-2"
            disabled={
              isDeleting ||
              isSubmitting ||
              (item.isGroup && childExpensesCount === 0)
            }
            onClick={onSubmitClick}>
            {isSubmitting ? (
              <>
                <FlairIcon icon="time-outline" size="xs" className="me-1" />
                {t('expenses.groups.submitting')}
              </>
            ) : (
              t('expenses.groups.submit')
            )}
          </button>
        </div>
      )}
    </div>
  );
};
